import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type QueryParams } from '@/lib/api/client';
import { leadsApi, type LeadTypeKey } from './api';
import { LeadDetailDialog } from './LeadDetailDialog';
import { NEWSLETTER_STATUSES } from './fields';
import { SOURCE_FILTERS, isSourceFilter, matchesSource, type SourceFilter } from './attribution';
import { SourceTag } from './LeadSource';
import { getLeadType } from '@/config/leadTypes';
import { keys } from '@/lib/query/keys';
import { useListState } from '@/lib/hooks/useListState';
import { useDebounced } from '@/lib/hooks/useDebounced';
import { useGate } from '@/lib/rbac/Gate';
import { useApiError } from '@/lib/query/useApiError';
import { isAppError } from '@/lib/api/errors';
import type { LeadStatus, NewsletterStatus, User } from '@/lib/api/types';
import { EXPORT_ROW_CAP } from '@/lib/config';
import { DataView } from '@/lib/ui/states';
import { DataTable, type Column } from '@/lib/ui/Table';
import { Pagination } from '@/lib/ui/Pagination';
import { PageHeader, MonoValue } from '@/lib/ui/Typography';
import { LeadStatusPill, NewsletterStatusPill } from '@/lib/ui/StatusPill';
import { Tag } from '@/lib/ui/Tag';
import { PermissionButton } from '@/lib/ui/Button';
import { Select, type SelectOption } from '@/lib/ui/Select';
import { TextInput } from '@/lib/ui/Field';
import { Switch } from '@/lib/ui/Switch';
import { useToast } from '@/lib/ui';
import { formatRelative } from '@/lib/format';

type Row = Record<string, unknown>;

/** Read a string-ish field off a heterogeneous lead row. */
function str(row: Row, key: string): string {
  const v = row[key];
  return v == null ? '' : String(v);
}

/** Triggers a browser download of a blob with the given filename. */
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Config-driven list for any of the four lead types (spec §11). Route: /leads/:type. */
export function LeadsPage() {
  const { t } = useTranslation();
  const { type = '' } = useParams();
  const cfg = getLeadType(type);
  const leadType = type as LeadTypeKey;
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const onError = useApiError();

  const { allowed: canRead } = useGate('leads:read');

  // Seed the status filter from the URL (dashboard deep-links, e.g. ?status=new).
  const initialStatus = searchParams.get('status') ?? '';
  // Contact only: ?kind=call deep-links straight to call requests.
  const initialKind = searchParams.get('kind') ?? '';
  const list = useListState({ status: initialStatus, kind: initialKind });
  const [rawSearch, setRawSearch] = useState('');
  const debounced = useDebounced(rawSearch, 300);
  const [spam, setSpam] = useState(false);
  // Source filter is client-side over the loaded page: the list API has no source filter.
  // Seeded from ?source= so a channel view can be linked.
  const initialSource = searchParams.get('source') ?? '';
  const [sourceFilter, setSourceFilter] = useState<SourceFilter | ''>(
    isSourceFilter(initialSource) ? initialSource : '',
  );
  const [active, setActive] = useState<Row | null>(null);

  // ?id= deep-links one lead (the "Open in admin" button in the team's
  // notification email). The fetched lead is shown until the dialog closes,
  // which also drops the param so a reload doesn't reopen it.
  const linkedId = searchParams.get('id') ?? '';
  const linked = useQuery({
    queryKey: keys.leads.detail(type, linkedId),
    queryFn: () => leadsApi.get(leadType, linkedId),
    enabled: !!cfg?.hasDetail && canRead && !!linkedId,
    retry: false,
  });
  useEffect(() => {
    if (linked.error) onError(linked.error);
  }, [linked.error, onError]);
  const shown: Row | null =
    active ?? (linkedId ? ((linked.data as Row | undefined) ?? null) : null);
  function closeDetail() {
    setActive(null);
    if (linkedId) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('id');
          return next;
        },
        { replace: true },
      );
    }
  }

  const isNewsletter = leadType === 'newsletter';
  const showSpamControls = !isNewsletter;

  // Admin users for the assignee filter + manage picker. Tolerate 403 by hiding.
  const usersQuery = useQuery({
    queryKey: keys.users.list({ per_page: 100 }),
    queryFn: () => api.get<User[]>('/admin/users', { per_page: 100 }),
    enabled: !!cfg && canRead && !isNewsletter,
    retry: false,
  });
  const usersForbidden = isAppError(usersQuery.error) && usersQuery.error.is('forbidden');
  const users: User[] = usersForbidden ? [] : (usersQuery.data ?? []);

  // Effective status: the Spam toggle wins over the status select for non-newsletter types.
  const selectedStatus = (list.filters.status as string) || '';
  const effectiveStatus = showSpamControls && spam ? 'spam' : selectedStatus;

  const queryParams = useMemo<QueryParams>(() => {
    const p: QueryParams = { ...list.params, q: debounced || undefined };
    if (showSpamControls) {
      // Spam hidden by default: only show when the Spam toggle is on.
      p.status = spam ? 'spam' : selectedStatus || undefined;
    }
    return p;
  }, [list.params, debounced, spam, showSpamControls, selectedStatus]);

  const query = useQuery({
    queryKey: keys.leads.list(leadType, queryParams),
    queryFn: () => leadsApi.list(leadType, queryParams),
    placeholderData: (prev) => prev,
    enabled: !!cfg,
  });

  const [exporting, setExporting] = useState(false);
  async function onExport() {
    setExporting(true);
    try {
      const res = await leadsApi.exportCsv(leadType, queryParams);
      downloadBlob(res.data, `leads-${leadType}.csv`);
      if (res.headers.get('X-Export-Truncated') === 'true') {
        toast.warning(
          t('leads.exportTruncatedTitle'),
          t('leads.exportTruncatedBody', { count: EXPORT_ROW_CAP }),
        );
      }
    } catch (err) {
      onError(err);
    } finally {
      setExporting(false);
    }
  }

  if (!cfg) return <p className="text-small text-ink-mute">{t('leads.unknownType')}</p>;

  // ─── Filters: status options ───────────────────────────────────────────────
  const statusOptions: SelectOption[] = isNewsletter
    ? [
        { value: '', label: t('leads.allStatuses') },
        ...NEWSLETTER_STATUSES.map((s) => ({ value: s, label: t(`leadStatus.${s}`) })),
      ]
    : [
        { value: '', label: t('leads.allStatuses') },
        ...cfg.statuses
          .filter((s) => s !== 'spam')
          .map((s) => ({ value: s, label: t(`leadStatus.${s}`) })),
      ];

  const kindOptions: SelectOption[] = [
    { value: '', label: t('leads.kind.all') },
    { value: 'call', label: t('leads.kind.call') },
    { value: 'message', label: t('leads.kind.message') },
  ];

  const sourceOptions: SelectOption[] = [
    { value: '', label: t('leads.source.all') },
    ...SOURCE_FILTERS.map((s) => ({ value: s, label: t(`leads.source.filter.${s}`) })),
  ];

  const assigneeOptions: SelectOption[] = [
    { value: '', label: t('leads.allAssignees') },
    ...users.map((u) => ({ value: u.id, label: u.name })),
  ];

  // ─── Columns ────────────────────────────────────────────────────────────────
  const columns: Column<Row>[] = buildColumns(leadType, users, t);

  // Newsletter confirmed/pending counts surfaced from the current page meta is not reliable;
  // show the header total and a per-page breakdown chip set instead.
  const pageRows = (query.data?.data ?? []) as unknown as Row[];
  const confirmedCount = pageRows.filter((r) => r.status === 'confirmed').length;
  const pendingCount = pageRows.filter((r) => r.status === 'pending_confirmation').length;

  return (
    <div>
      <PageHeader
        eyebrow={t(`nav.${cfg.navKey}`).toUpperCase()}
        title={t(`nav.${cfg.navKey}`)}
        count={query.data?.meta?.pagination.total}
        actions={
          cfg.hasExport ? (
            <PermissionButton
              allowed={canRead}
              reason={t('leads.readPermissionReason')}
              variant="ghost"
              loading={exporting}
              onClick={onExport}
            >
              {t('leads.exportCsv')}
            </PermissionButton>
          ) : undefined
        }
      />

      {isNewsletter && pageRows.length > 0 && (
        <div className="mb-md flex flex-wrap items-center gap-sm">
          <Tag variant="teal">{t('leads.confirmedPage', { count: confirmedCount })}</Tag>
          <Tag variant="amber">{t('leads.pendingPage', { count: pendingCount })}</Tag>
        </div>
      )}

      {/* Filters */}
      <div className="mb-md flex flex-wrap items-end gap-sm">
        <div className="w-[200px]">
          <Select
            label={t('common.status')}
            value={(list.filters.status as string) || ''}
            onValueChange={(v) => list.setFilter('status', v || undefined)}
            options={statusOptions}
            disabled={showSpamControls && spam}
          />
        </div>

        {leadType === 'contact' && (
          <div className="w-[180px]">
            <Select
              label={t('leads.kind.label')}
              value={(list.filters.kind as string) || ''}
              onValueChange={(v) => list.setFilter('kind', v || undefined)}
              options={kindOptions}
            />
          </div>
        )}

        <div className="w-[180px]">
          <Select
            label={t('leads.source.label')}
            value={sourceFilter}
            onValueChange={(v) => setSourceFilter(isSourceFilter(v) ? v : '')}
            options={sourceOptions}
          />
        </div>

        {!isNewsletter && !usersForbidden && (
          <div className="w-[200px]">
            <Select
              label={t('leads.assignee')}
              value={(list.filters.assigned_to as string) || ''}
              onValueChange={(v) => list.setFilter('assigned_to', v || undefined)}
              options={assigneeOptions}
            />
          </div>
        )}

        <div className="w-[160px]">
          <TextInput
            label={t('leads.from')}
            type="date"
            value={(list.filters.from as string) || ''}
            onChange={(e) => list.setFilter('from', e.target.value || undefined)}
          />
        </div>
        <div className="w-[160px]">
          <TextInput
            label={t('leads.to')}
            type="date"
            value={(list.filters.to as string) || ''}
            onChange={(e) => list.setFilter('to', e.target.value || undefined)}
          />
        </div>

        {!isNewsletter && (
          <div className="pb-[6px]">
            <Switch
              label={t('leads.newOnly')}
              checked={(list.filters.status as string) === 'new'}
              onCheckedChange={(v) => list.setFilter('status', v ? 'new' : undefined)}
            />
          </div>
        )}

        {showSpamControls && (
          <div className="pb-[6px]">
            <Switch label={t('leads.spam')} checked={spam} onCheckedChange={(v) => setSpam(v)} />
          </div>
        )}

        <div className="min-w-[180px] flex-1">
          <TextInput
            label={t('common.search')}
            value={rawSearch}
            onChange={(e) => setRawSearch(e.target.value)}
          />
        </div>
      </div>

      {effectiveStatus === 'spam' && (
        <p className="mb-sm text-small text-ink-mute">{t('leads.showingSpam')}</p>
      )}

      <DataView query={query} isEmpty={(d) => d.data.length === 0}>
        {(d) => {
          const rows = (d.data as unknown as Row[]).filter((r) =>
            matchesSource(r.source, sourceFilter),
          );
          return (
            <>
              {sourceFilter && (
                <p className="mb-sm text-small text-ink-mute" role="status">
                  {t('leads.source.pageNote', { shown: rows.length, total: d.data.length })}
                </p>
              )}
              {rows.length > 0 && (
                <DataTable
                  columns={columns}
                  rows={rows}
                  rowKey={(r) => str(r, 'id')}
                  onRowClick={(r) => setActive(r)}
                />
              )}
              {d.meta?.pagination && (
                <Pagination
                  meta={d.meta.pagination}
                  onPageChange={list.setPage}
                  onPerPageChange={list.setPerPage}
                />
              )}
            </>
          );
        }}
      </DataView>

      {shown && (
        <LeadDetailDialog
          type={leadType}
          cfg={cfg}
          row={shown}
          users={users}
          open
          onOpenChange={(open) => {
            if (!open) closeDetail();
          }}
        />
      )}
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function buildColumns(
  type: LeadTypeKey,
  users: User[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): Column<Row>[] {
  const assigneeName = (id: string): string =>
    (id && users.find((u) => u.id === id)?.name) || id || '—';

  const sourceCol: Column<Row> = {
    id: 'source',
    header: t('leads.source.label'),
    cell: (r) => <SourceTag source={r.source} />,
  };

  const created: Column<Row> = {
    id: 'created',
    header: t('common.created'),
    align: 'end',
    cell: (r) => <span className="text-ink-mute">{formatRelative(str(r, 'created_at'))}</span>,
  };

  if (type === 'newsletter') {
    return [
      {
        id: 'email',
        header: t('leads.colEmail'),
        cell: (r) => <span className="font-medium text-ink-deep">{str(r, 'email')}</span>,
      },
      {
        id: 'status',
        header: t('common.status'),
        cell: (r) => <NewsletterStatusPill status={r.status as NewsletterStatus} />,
      },
      {
        id: 'locale',
        header: t('leads.colLocale'),
        cell: (r) => <MonoValue className="text-ink-mid">{str(r, 'locale')}</MonoValue>,
      },
      sourceCol,
      created,
    ];
  }

  const nameCol: Column<Row> = {
    id: 'name',
    header: t('leads.colName'),
    cell: (r) => (
      <div>
        <div className="font-medium text-ink-deep">{str(r, 'full_name') || '—'}</div>
        <div className="text-ink-mute">{str(r, 'email')}</div>
      </div>
    ),
  };
  const statusCol: Column<Row> = {
    id: 'status',
    header: t('common.status'),
    cell: (r) => <LeadStatusPill status={r.status as LeadStatus} />,
  };
  const assigneeCol: Column<Row> = {
    id: 'assignee',
    header: t('leads.assignee'),
    cell: (r) => <span className="text-ink-mid">{assigneeName(str(r, 'assigned_to'))}</span>,
  };

  if (type === 'contact') {
    return [
      nameCol,
      {
        id: 'company',
        header: t('leads.colCompany'),
        cell: (r) => <span className="text-ink-mid">{str(r, 'company') || '—'}</span>,
      },
      {
        // Every site lead is `commercial`, so the useful distinction is call vs message.
        id: 'request',
        header: t('leads.colRequest'),
        cell: (r) =>
          str(r, 'request_kind') === 'call' ? (
            <Tag variant="teal">
              {t('leads.kind.call')} · {t(`leads.call.channel.${str(r, 'call_channel')}`)}
            </Tag>
          ) : (
            <Tag variant="neutral">{t('leads.kind.message')}</Tag>
          ),
      },
      sourceCol,
      statusCol,
      assigneeCol,
      created,
    ];
  }

  if (type === 'applications') {
    return [
      nameCol,
      {
        id: 'cv',
        header: t('leads.colCv'),
        cell: (r) =>
          r.resume_media_id ? (
            <Tag variant="teal">{t('leads.cvYes')}</Tag>
          ) : (
            <Tag variant="amber">{t('leads.cvNo')}</Tag>
          ),
      },
      sourceCol,
      statusCol,
      assigneeCol,
      created,
    ];
  }

  // quotes
  return [
    nameCol,
    {
      id: 'company',
      header: t('leads.colCompany'),
      cell: (r) => <span className="text-ink-mid">{str(r, 'company') || '—'}</span>,
    },
    sourceCol,
    statusCol,
    assigneeCol,
    created,
  ];
}
